<script setup lang="ts">
const {
  name = "",
  step = 1,
  min = 0,
  max = 9999999999,
  disabled = false,
  larger = false,
  testAttribPrefix = "",
} = defineProps<{
  name?: string;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  larger?: boolean;
  testAttribPrefix?: string;
}>();

const model = defineModel<number>({ required: true });

const emit = defineEmits(["update:model.value"]);

const inputClass = computed(() => ({
  disabled,
  larger,
}));

const incrementDisabled = computed(() => disabled || model.value >= max);
const decrementDisabled = computed(() => disabled || model.value <= min);

const increment = () => {
  model.value = Math.min(max, Number(model.value) + step);
};

const decrement = () => {
  model.value = Math.max(min, Number(model.value) - step);
};
</script>

<template>
  <div class="input-wrap-radio" :class="inputClass">
    <button
      class="down btn"
      tabindex="0"
      :disabled="decrementDisabled"
      type="button"
      @click="decrement"
      aria-label="decrement"
      :data-testid="`${testAttribPrefix}plus-button`"
    >
      <IconMinus />
    </button>

    <input
      :step="step"
      :disabled="disabled"
      :min="min"
      :max="max"
      tabindex="0"
      size="3"
      type="number"
      class="input"
      :name="name"
      :id="`input_${name}`"
      v-model="model"
      @keydown.up.prevent="increment"
      @keydown.down.prevent="decrement"
      :aria-label="name"
      :data-testid="`${testAttribPrefix}value`"
    />

    <button
      class="up btn"
      :disabled="incrementDisabled"
      tabindex="0"
      type="button"
      @click="increment"
      aria-label="increment"
      :data-testid="`${testAttribPrefix}minus-button`"
    >
      <IconPlus />
    </button>
  </div>
</template>

<style scoped lang="scss">
.input-wrap-radio {
  position: relative;
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  color: var(--cm-color-black, #000);
  transition: var(--cm-transition-fast, 200ms);

  &.valid {
    border-color: var(--color-green);
  }

  &.invalid {
    border-color: var(--primary-red);
  }

  &.disabled {
    pointer-events: none;

    input {
      color: var(--color-black-disabled);
    }
  }

  &.larger {
    input {
      width: 3em;
    }
  }
}

.input {
  position: relative;
  width: 2em;
  padding: 0;
  padding: var(--cm-spacing-1, 0.25rem) var(--cm-spacing-2, 0.5rem);
  color: var(--cm-color-black, #000);
  font-size: var(--font-size-btn, 1.125rem);
  font-family: var(
    --font-family-bold,
    "halet-newyorknineteensixty, avenir, helvetica, arial, sans-serif"
  );
  line-height: var(--line-height-btn, 1.5);
  text-align: center;
  border: none;
  transition: var(--cm-transition-fast, 200ms);

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
  }

  &[type="number"] {
    margin: 0 2px;
    appearance: textfield;
  }

  &:focus {
    border: none;
    outline: none;
  }
}

.btn {
  width: 30px;
  height: 30px;
  padding: 0;
  overflow: hidden;
  background: none;
  border: none;
  cursor: pointer;
  appearance: none;

  circle,
  :deep(circle) {
    transition: var(--cm-transition-fast, 200ms);
    stroke: var(--cm-color-black, #000);
  }

  path,
  :deep(path) {
    transition: var(--cm-transition-fast, 200ms);
    fill: var(--cm-color-black, #000);
  }

  &:hover {
    circle,
    :deep(circle) {
      stroke: var(--color-black-hover);
    }

    path,
    :deep(path) {
      fill: var(--color-black-hover);
    }
  }

  &:focus {
    circle,
    :deep(circle) {
      stroke: var(--color-black-focus);
    }

    path,
    :deep(path) {
      fill: var(--color-black-focus);
    }
  }

  &:active {
    circle,
    :deep(circle) {
      stroke: var(--color-black-pressed);
    }

    path,
    :deep(path) {
      fill: var(--color-black-pressed);
    }
  }

  &:disabled {
    cursor: default;

    circle,
    :deep(circle) {
      stroke: var(--color-black-disabled-surface);
    }

    path,
    :deep(path) {
      fill: var(--color-black-disabled-surface);
    }
  }
}
</style>
